import { useEffect, useState } from 'react';

import { FieldValues, UseFormReturn } from 'react-hook-form';

import { SubmitDateRange } from '@/modules/common/components/templates/feature-details';
import {
  ConflictFieldConfig,
  ConflictSnapshot,
  PatchEntityEffect,
} from '@/modules/projects/types/project-conflicts';
import { formatDateToNonISOFormat } from '@/utils/date-formatters';
import { areValuesEqual } from '@/utils/form-state.utils';
import { logger } from '@/utils/logger/logger';

import { TeamMemberFormData } from '../types';

interface UseConflictResolutionParams<TFormData extends FieldValues, TFieldKey extends string> {
  /**
   * Tag for logging purposes
   */
  tag: string;
  /**
   * Form instance from react-hook-form
   */
  form: UseFormReturn<TFormData>;
  /**
   * Current team member ID (undefined in create mode)
   */
  teamMemberId?: string;
  /**
   * Callback to check for conflicts
   * Returns conflict data with hasConflicts flag and items array
   */
  checkConflicts: (params: {
    teamMemberId: string;
    startDate: string;
    endDate: string | null;
  }) => Promise<{
    hasConflicts: boolean;
    items: Array<{
      id: number;
      effectiveStartDate: string;
      effectiveEndDate: string | null;
      patchEffect: PatchEntityEffect;
      snapshot: any;
    }>;
  }>;
  /**
   * Whether conflict checking is in progress
   */
  isCheckingConflicts: boolean;
  /**
   * Callback to notify parent about form state changes
   */
  onFormStateChange?: (isDirty: boolean) => void;
  /**
   * Callback to get current form data with all fields
   */
  getFormData: () => TeamMemberFormData;
  /**
   * Callback to directly save data to API after conflict resolution
   */
  onDirectSave?: (
    data: TeamMemberFormData,
    effectiveStartDate?: Date,
    effectiveEndDate?: Date | null
  ) => Promise<void>;
  /**
   * Field configurations for determining which fields to check for conflicts
   */
  fields?: ConflictFieldConfig<TFieldKey>[];
  /**
   * Current form values for conflict comparison (can be enriched with display names)
   */
  currentFormValues?: Record<TFieldKey, unknown>;
}

interface UseConflictResolutionReturn<TFieldKey extends string> {
  /**
   * Whether to show the conflict resolution modal
   */
  showConflictModal: boolean;
  /**
   * Conflict snapshots to display in the modal
   */
  conflictSnapshots: ConflictSnapshot[];
  /**
   * Selected date range from the footer
   */
  selectedDateRange: { from: Date; to?: Date | null } | null;
  /**
   * Close the conflict modal
   */
  closeConflictModal: () => void;
  /**
   * Handle form submission with conflict detection
   * @param baseHandleSubmit - Base submit handler from useTabFormState
   * @param dateRange - Optional date range from footer
   */
  handleSubmitWithConflicts: (
    baseHandleSubmit: () => Promise<void>,
    dateRange?: SubmitDateRange
  ) => Promise<void>;
  /**
   * Handle conflict resolution by merging resolved values with current form data
   * @param resolvedValues - Resolved values from the conflict modal
   */
  handleResolveConflicts: (resolvedValues: Record<TFieldKey, any>) => Promise<void>;
}

/**
 * Reusable hook for handling conflict resolution in team member detail tabs
 * Encapsulates conflict detection, modal state management, and resolution logic
 */
/**
 * Checks if a field has any differences between form value and snapshot values
 */
function hasFieldDifferences<TFieldKey extends string>(
  field: ConflictFieldConfig<TFieldKey>,
  formValues: Record<TFieldKey, unknown>,
  snapshots: ConflictSnapshot[]
): boolean {
  const snapshotKey = field.snapshotKey || field.key;
  const formValue = formValues[field.key];

  // Get comparable form value
  const comparableFormValue = field.getFormValueForComparison
    ? field.getFormValueForComparison(formValue)
    : formValue;

  // Check if form value differs from any snapshot value
  for (const snapshot of snapshots) {
    const rawSnapshotValue = snapshot.data[snapshotKey];
    const comparableSnapshotValue = field.getSnapshotValueForComparison
      ? field.getSnapshotValueForComparison(rawSnapshotValue)
      : rawSnapshotValue;

    if (!areValuesEqual(comparableFormValue, comparableSnapshotValue)) {
      return true;
    }
  }

  // Also check if snapshots differ from each other
  for (let i = 0; i < snapshots.length; i++) {
    for (let j = i + 1; j < snapshots.length; j++) {
      const snapshotKeyI = field.snapshotKey || field.key;
      const valueI = field.getSnapshotValueForComparison
        ? field.getSnapshotValueForComparison(snapshots[i].data[snapshotKeyI])
        : snapshots[i].data[snapshotKeyI];
      const valueJ = field.getSnapshotValueForComparison
        ? field.getSnapshotValueForComparison(snapshots[j].data[snapshotKeyI])
        : snapshots[j].data[snapshotKeyI];
      if (!areValuesEqual(valueI, valueJ)) {
        return true;
      }
    }
  }

  return false;
}

/**
 * Checks if there are any actual field differences that would require showing the conflict modal
 */
function hasActualConflicts<TFieldKey extends string>(
  fields: ConflictFieldConfig<TFieldKey>[],
  formValues: Record<TFieldKey, unknown>,
  snapshots: ConflictSnapshot[]
): boolean {
  for (const field of fields) {
    // Skip excluded fields
    if (field.alwaysExcluded) continue;

    if (hasFieldDifferences(field, formValues, snapshots)) {
      return true;
    }
  }
  return false;
}

export function useTeamMemberChangeConflictResolution<
  TFormData extends FieldValues,
  TFieldKey extends string,
>({
  tag,
  form,
  teamMemberId,
  checkConflicts,
  isCheckingConflicts,
  onFormStateChange,
  getFormData,
  onDirectSave,
  fields,
  currentFormValues,
}: UseConflictResolutionParams<TFormData, TFieldKey>): UseConflictResolutionReturn<TFieldKey> {
  // Conflict resolution state
  const [showConflictModal, setShowConflictModal] = useState(false);
  const [conflictSnapshots, setConflictSnapshots] = useState<ConflictSnapshot[]>([]);
  const [selectedDateRange, setSelectedDateRange] = useState<{
    from: Date;
    to?: Date | null;
  } | null>(null);

  // Notify parent about conflict checking loading state changes.
  // Calling onFormStateChange(false) while isCheckingConflicts is true disables the save button
  // and triggers a page re-render so the parent can pick up isCheckingConflicts from the tab ref.
  useEffect(() => {
    if (isCheckingConflicts && onFormStateChange) {
      // Force a parent re-render while conflict check is in flight so the footer
      // reads the latest ref state and disables the submit button.
      onFormStateChange(false);
    }
  }, [isCheckingConflicts, onFormStateChange]);

  /**
   * Submit handler with conflict detection
   */
  const handleSubmitWithConflicts = async (
    baseHandleSubmit: () => Promise<void>,
    dateRange?: SubmitDateRange
  ): Promise<void> => {
    try {
      // Skip conflict detection if no team member ID (create mode or missing data)
      if (!teamMemberId) {
        await baseHandleSubmit();
        return;
      }

      // Skip conflict detection if no date range provided
      // This happens when only non-versioned fields changed
      if (!dateRange?.from) {
        await baseHandleSubmit();
        return;
      }

      const startDate = formatDateToNonISOFormat(dateRange.from);
      const endDate = dateRange.to ? formatDateToNonISOFormat(dateRange.to) : null;

      // Check for conflicts using the hook (handles loading state and error toasts)
      const conflictData = await checkConflicts({
        teamMemberId,
        startDate,
        endDate,
      });

      // If BE reports conflicts, check if there are actual field differences
      if (conflictData.hasConflicts) {
        const snapshots: ConflictSnapshot[] = conflictData.items.map(item => ({
          id: String(item.id),
          effectiveStartDate: item.effectiveStartDate,
          effectiveEndDate: item.effectiveEndDate,
          patchEffect: item.patchEffect,
          data: item.snapshot,
        }));

        // Check if there are actual field differences that require showing the modal
        // If fields config and currentFormValues are provided, do the check
        const formValuesToCheck =
          currentFormValues || (form.getValues() as Record<TFieldKey, unknown>);
        const hasRealConflicts = fields
          ? hasActualConflicts(fields, formValuesToCheck, snapshots)
          : true; // Default to true if no fields config

        if (hasRealConflicts) {
          setConflictSnapshots(snapshots);
          setSelectedDateRange({ from: dateRange.from, to: dateRange.to });
          setShowConflictModal(true);

          // Throw to prevent the form manager from continuing with submission
          throw new Error('CONFLICT_RESOLUTION_REQUIRED');
        }
        // If no real field conflicts, fall through to direct save below
      }

      // No conflicts, but we still need to save with the selected date range
      if (onDirectSave && teamMemberId) {
        const currentFormData = getFormData();
        await onDirectSave(
          {
            ...currentFormData,
            id: teamMemberId,
          } as TeamMemberFormData,
          dateRange.from,
          dateRange.to
        );
        // Throw to prevent form manager from continuing with submission after direct save
        throw new Error('SAVE_COMPLETED');
      }

      // Fallback to base submit if no onDirectSave callback
      await baseHandleSubmit();
    } catch (error) {
      // Re-throw special flow control errors without logging (expected flows)
      if (
        error instanceof Error &&
        (error.message === 'CONFLICT_RESOLUTION_REQUIRED' || error.message === 'SAVE_COMPLETED')
      ) {
        throw error;
      }

      // Restore footer interactivity after a failed conflict lookup.
      // The user still has unsaved changes and should be able to retry.
      onFormStateChange?.(true);

      // Other errors are already handled by the hook's onError callback
      logger.error(tag, 'Form submission error:', error);
      throw error;
    }
  };

  /**
   * Converts resolved values to API-compatible format
   * Entity objects like { id: 123, name: "Developer" } are converted to their id (number)
   */
  const convertResolvedValuesToApiFormat = (
    resolvedValues: Record<TFieldKey, unknown>
  ): Record<string, unknown> => {
    const converted: Record<string, unknown> = {};

    for (const [key, value] of Object.entries(resolvedValues)) {
      if (value && typeof value === 'object' && 'id' in value) {
        // Entity object - extract the id
        converted[key] = (value as { id: number }).id;
      } else {
        converted[key] = value;
      }
    }

    return converted;
  };

  /**
   * Handle conflict resolution by merging resolved values with current form data and saving
   */
  const handleResolveConflicts = async (
    resolvedValues: Record<TFieldKey, unknown>
  ): Promise<void> => {
    try {
      // Get the current form data (unchanged fields)
      const currentFormData = getFormData();

      // Convert resolved values to API format (extract IDs from entity objects)
      const apiCompatibleResolvedValues = convertResolvedValuesToApiFormat(resolvedValues);

      // Merge current form data with resolved values from conflict modal
      // The resolved values take precedence for conflicting fields
      const mergedData = {
        ...currentFormData,
        ...apiCompatibleResolvedValues,
      };

      // If onDirectSave callback is provided, use it to save directly to API
      if (onDirectSave && teamMemberId && selectedDateRange) {
        await onDirectSave(
          {
            ...mergedData,
            id: teamMemberId,
          } as TeamMemberFormData,
          selectedDateRange.from,
          selectedDateRange.to
        );

        // Save succeeded — close the modal and update form with the resolved values
        setShowConflictModal(false);

        // Convert entity objects back to string IDs for the form
        Object.entries(resolvedValues).forEach(([key, value]) => {
          let formValue: unknown = value;
          if (value && typeof value === 'object' && 'id' in value) {
            // Convert entity object to string ID for the form
            formValue = String((value as { id: number }).id);
          }
          form.setValue(
            key as Parameters<typeof form.setValue>[0],
            formValue as Parameters<typeof form.setValue>[1],
            {
              shouldValidate: false,
              shouldDirty: false,
            }
          );
        });
      }
    } catch (error) {
      logger.error(tag, 'Error resolving conflicts:', error);
      // Keep the modal open so the user can retry.
      // Restore footer interactivity so the date picker and save button are usable
      // if the user closes the modal manually.
      onFormStateChange?.(true);
      throw error;
    }
  };

  const closeConflictModal = () => {
    setShowConflictModal(false);
    // Restore dirty state after user cancels conflict resolution.
    // The form still has unsaved changes and the conflict check is no longer running.
    // This also triggers a page re-render so the footer reads isCheckingConflicts=false
    // and stops showing the loading spinner.
    if (onFormStateChange) {
      onFormStateChange(true);
    }
  };

  return {
    showConflictModal,
    conflictSnapshots,
    selectedDateRange,
    closeConflictModal,
    handleSubmitWithConflicts,
    handleResolveConflicts,
  };
}
